/* 
   VERY IMPORTANT: This file is shared by the iframe and portal.
   VERY IMPORTANT: The master copy of this file lives at civic-pass/shared/.
   VERY IMPORTANT: Make all changes there, then run `yarn copyAll` to copy it to the correct locations.
*/

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import DarkModeToggle from './DarkModeToggle';
import facetecService from '../../services/faceAuthentication/facetec/facetecService';
import documentCapture from '../../services/documentCapture';
import OnfidoDocumentCapture from '../../services/documentCapture/onfidoDocumentCapture';
import isVersionGreaterOrEqual from '../../utils/version';
import Log from '../../common/logging';
import WindowProvider from '../../common/window';

export enum Theme {
  DARK = 'dark-theme',
  LIGHT = 'light-theme',
}

interface ThemeContextInterface {
  darkMode: boolean;
  toggleDarkMode: () => void;
  darkModeSupported: boolean;
}

export const ThemeContext = React.createContext<ThemeContextInterface>({} as ThemeContextInterface);

// Dark mode is supported only if the RC/gateway client version is given and is greater than the minimum required version.
// Also if we're not in an iframe
export function isDarkModeSupported(clientVersion: string): boolean {
  // The iframe requires the outer client to support dark mode before it will be enabled.
  const darkModeMinimumClientVersions: { [client: string]: string | undefined } = {
    '@civic/gateway-client-react': process.env.REACT_APP_DARKMODE_MINIMUM_GATEWAY_CLIENT_VERSION || undefined,
    '@civic/solana-gateway-react': process.env.REACT_APP_DARKMODE_MINIMUM_SOLANA_GATEWAY_VERSION || undefined,
    '@civic/ethereum-gateway-react': process.env.REACT_APP_DARKMODE_MINIMUM_ETHEREUM_GATEWAY_VERSION || undefined,
  };

  // We're not in an iframe (e.g. mobile handover flow)
  if (!WindowProvider.isInIframe()) {
    return true;
  }

  // If the client version is not provided or is not in the expected format, we will not enable dark mode.
  if (!clientVersion || !clientVersion.includes(':')) {
    return false;
  }
  const [client, version] = clientVersion.split(':');
  const minimumVersion = darkModeMinimumClientVersions[client];

  // Something went wrong when determining the minimum client version
  if (!minimumVersion) {
    Log.error(`Could not determine dark mode support for unknown client: ${client}`);
    return false;
  }

  // Finally check that the client version is greater than the minimum required version
  const supported = isVersionGreaterOrEqual(version, minimumVersion);
  return supported;
}

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme | null>(Theme.DARK);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [darkModeSupported, setDarkModeSupported] = useState<boolean>(false);
  const isDev = (process.env.REACT_APP_STAGE || 'unknown') === 'dev';
  const queryParams = queryString.parse(window.location.search);

  useEffect(() => {
    const {
      civicClient, // The client version, either of the core or one of the framework-specific components.
    } = queryParams || {};

    // If the outer RC/gateway client does not support dark mode, we will not enable it.
    // Mobile handoff flow runs outside of the RC/gateway client, so we don't need to check for dark mode support.
    if (!isDarkModeSupported(civicClient as string)) {
      setDarkMode(false);
      setDarkModeSupported(false);
      Log.debug(`Dark mode is not supported by the client version ${civicClient}`);
      return;
    }

    // Else we read the system setting
    setDarkModeSupported(true);
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    setDarkMode(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [JSON.stringify(queryParams)]);

  // change class on the document component to the selected theme
  const changeTheme = useCallback(
    (selectedTheme: Theme) => {
      const root = document.documentElement;
      if (theme) root.classList.remove(theme);
      root.classList.add(selectedTheme);
      setTheme(selectedTheme);
    },
    [theme, setTheme],
  );

  useEffect(() => {
    // Themed external SDKS need to be informed of the theme change
    facetecService.setDarkMode(darkMode);
    if (documentCapture instanceof OnfidoDocumentCapture) {
      documentCapture?.setDarkMode(darkMode);
    }
    if (darkMode) {
      changeTheme(Theme.DARK);
    } else {
      changeTheme(Theme.LIGHT);
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const context = useMemo(
    () => ({ darkMode, toggleDarkMode, darkModeSupported }),
    [darkMode, toggleDarkMode, darkModeSupported],
  );

  return (
    <ThemeContext.Provider value={context}>
      {children}
      {isDev && darkModeSupported && <DarkModeToggle toggleDarkMode={toggleDarkMode} darkMode={darkMode} />}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export { ThemeProvider, useTheme };
